import React from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button, { type ButtonProps } from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import classnames from 'classnames';
import ImageIcon from '@/components/common/ImageIcon';
import IconSizes from '@/constants/ui/IconSizes';
import translate from '@/lib/intl/translate';

type DialogHeaderProps = {
  ['data-testid']?: string;
  headerText: React.ReactNode;
  onClose: () => void;
};

export function DialogHeader({
  'data-testid': dataTestid = 'custom-dialog',
  headerText,
  onClose,
}: DialogHeaderProps) {
  return (
    <div
      className="flex items-baseline justify-between"
      data-testid={dataTestid}
    >
      <DialogTitle variant="h3" className="type-h3 sm:type-h2">
        {headerText}
      </DialogTitle>
      <Button data-testid={`${dataTestid}-close-btn`} onClick={onClose}>
        <ImageIcon
          width={IconSizes.SM}
          height={IconSizes.SM}
          type="close"
          alt={translate('MULTI_ACTION_CLOSE')}
        />
      </Button>
    </div>
  );
}

type DialogActionButtonProps = ButtonProps & {
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  showLoader?: boolean;
};

export function DialogActionButton({
  children = translate('SUBMIT'),
  disabled = false,
  onClick = () => {},
  showLoader = false,
  ...buttonProps
}: DialogActionButtonProps) {
  return (
    <LoadingButton
      className="w-full text-base sm:w-auto"
      data-testid="custom-dialog-action-btn"
      disableElevation
      disabled={disabled}
      loading={showLoader}
      onClick={onClick}
      variant="contained"
      {...buttonProps}
    >
      {children}
    </LoadingButton>
  );
}

type DialogCancelButtonProps = ButtonProps & {
  children?: React.ReactNode;
  onClick: () => void;
};

export function DialogCancelButton({
  children = translate('CANCEL'),
  onClick,
  ...buttonProps
}: DialogCancelButtonProps) {
  return (
    <Button
      className="w-full text-base sm:w-auto"
      data-testid="custom-dialog-cancel-btn"
      disableElevation
      onClick={onClick}
      {...buttonProps}
    >
      {children}
    </Button>
  );
}

type DialogActionProps = {
  actionButtonLabel?: string;
  children?: React.ReactNode;
  className?: string;
  disableActionButton?: boolean;
  handleActionButton?: () => void;
  onClose?: () => void;
  showCancel?: boolean;
};

export function DialogActions({
  actionButtonLabel = translate('SUBMIT'),
  children = null,
  className = '',
  disableActionButton = false,
  handleActionButton = () => {},
  onClose = () => {},
  showCancel = true,
}: DialogActionProps) {
  return (
    <MuiDialogActions
      className={classnames(
        'flex flex-col flex-wrap justify-start sm:flex-row-reverse',
        className,
      )}
      disableSpacing
    >
      {children || (
        <>
          <DialogActionButton
            showLoader={disableActionButton}
            onClick={handleActionButton}
          >
            {actionButtonLabel}
          </DialogActionButton>
          {showCancel && <DialogCancelButton onClick={onClose} />}
        </>
      )}
    </MuiDialogActions>
  );
}

type CustomDialogContentProps = {
  actionButtonLabel: string;
  handleActionButton: () => void;
  disableActionButton?: boolean;
  dialogActions?: React.ReactNode;
  dialogHeader: string;
  onClose: () => void;
  showCancel?: boolean;
  showDialogActions: boolean;
  children?: React.ReactNode;
};

export function CustomDialogContent({
  actionButtonLabel,
  handleActionButton,
  disableActionButton = false,
  dialogActions = null,
  dialogHeader,
  onClose,
  showCancel = false,
  showDialogActions,
  children = null,
}: CustomDialogContentProps) {
  return (
    <>
      {dialogHeader && (
        <DialogHeader headerText={dialogHeader} onClose={onClose} />
      )}
      {children && (
        <DialogContent className="hide-scrollbar" dividers>
          {children}
        </DialogContent>
      )}

      {showDialogActions && (
        <DialogActions
          actionButtonLabel={actionButtonLabel}
          handleActionButton={handleActionButton}
          disableActionButton={disableActionButton}
          onClose={onClose}
          showCancel={showCancel}
        >
          {dialogActions}
        </DialogActions>
      )}
    </>
  );
}

type CustomDialogProps = DialogProps & {
  actionButtonLabel?: string;
  handleActionButton?: () => void;
  disableActionButton?: boolean;
  dialogActions?: React.ReactNode;
  dialogHeader?: string;
  onClose?: () => void;
  open: boolean;
  showCancel?: boolean;
  showDialogActions?: boolean;
  children?: React.ReactNode;
};

export default function CustomDialog({
  actionButtonLabel = translate('SUBMIT'),
  children = null,
  dialogActions = null,
  disableActionButton = false,
  dialogHeader = '',
  handleActionButton = () => {},
  onClose = () => {},
  open,
  showCancel = true,
  showDialogActions = true,
  ...dialogProps
}: CustomDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} {...dialogProps}>
      <CustomDialogContent
        actionButtonLabel={actionButtonLabel}
        handleActionButton={handleActionButton}
        disableActionButton={disableActionButton}
        dialogActions={dialogActions}
        dialogHeader={dialogHeader}
        showCancel={showCancel}
        onClose={onClose}
        showDialogActions={showDialogActions}
      >
        {children}
      </CustomDialogContent>
    </Dialog>
  );
}
